import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Link } from "react-router-dom";
import { FileText, FileSpreadsheet, Trash2 } from "lucide-react";
import { useEccSrps, useDeleteSrp } from "@/hooks/useEcc";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/utils";
import type { EccArea, EccSrpSummary } from "@/api/types";

const STATUS_LABEL: Record<string, string> = {
  official: "Oficial",
  draft: "Borrador",
  proposed: "Propuesto",
  rejected: "Rechazado",
  archived: "Archivado",
};

const STATUS_CLASS: Record<string, string> = {
  official: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  draft: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  proposed: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  rejected: "bg-destructive/15 text-destructive",
  archived: "bg-muted text-muted-foreground",
};

function SrpRow({
  srp,
  area,
  canDelete,
  onDelete,
}: {
  srp: EccSrpSummary;
  area: EccArea;
  canDelete: boolean;
  onDelete: (id: string, name: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
      {srp.output_format === "docx" ? (
        <FileText className="size-5 shrink-0 text-blue-500" />
      ) : (
        <FileSpreadsheet className="size-5 shrink-0 text-emerald-500" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{srp.name}</div>
        <div className="text-xs text-muted-foreground truncate">{srp.description}</div>
      </div>
      <span
        className={cn(
          "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
          STATUS_CLASS[srp.status] ?? STATUS_CLASS.draft,
        )}
      >
        {STATUS_LABEL[srp.status] ?? srp.status}
      </span>
      <Link
        to={`/ecc/new?area=${area}&srp=${srp.id}`}
        className="shrink-0 rounded-md border px-3 py-1 text-xs font-medium transition-colors hover:bg-accent"
      >
        Correr
      </Link>
      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(srp.id, srp.name)}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          title="Borrar SRP"
        >
          <Trash2 className="size-4" />
        </button>
      )}
    </div>
  );
}

export function EccCatalogPage() {
  const { area } = useOutletContext<{ area: EccArea }>();
  const { data, isPending, error } = useEccSrps(area);
  const { user } = useAuth();
  const deleteMutation = useDeleteSrp();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const items = data?.items ?? [];
  const canDelete = user?.role === "admin";

  function handleDelete(id: string, name: string) {
    if (!window.confirm(`¿Borrar el SRP "${name}"? Esta acción no se puede deshacer.`)) return;
    setDeleteError(null);
    deleteMutation.mutate(id, {
      onError: (err) => {
        const msg =
          err instanceof ApiError ? err.message : "Error al borrar el SRP.";
        setDeleteError(msg);
      },
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          SRPs del área <span className="font-medium text-foreground">{area.toUpperCase()}</span>.
          Solo se pueden ejecutar los oficiales.
        </p>
      </div>

      {isPending && !data && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Error al cargar el catálogo. El backend ECC aún no está activo.
        </div>
      )}

      {deleteError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {deleteError}
        </div>
      )}

      {!isPending && items.length === 0 && !error && (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          {area === "custodias"
            ? "Sin SRPs todavía — pendiente DPECC-F (levantamiento de criterios con el área)."
            : "No hay SRPs configurados para esta área."}
        </div>
      )}

      <div className="space-y-2">
        {items.map((srp) => (
          <SrpRow
            key={srp.id}
            srp={srp}
            area={area}
            canDelete={canDelete}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
}
